import React, { useEffect, useState } from 'react';
import axios from 'axios';
import Button from '../../../../components/ui/Button';
import { API_ENDPOINTS } from '../../../../utils/config';
import TaxInvoiceForm from '../../sales/components/TaxInvoiceForm';
import {
  FileText,
  Search,
  AlertCircle,
  CheckCircle,
  Eye,
  Edit,
  Trash2,
  Plus,
  Check,
  Clock,
  X
} from 'lucide-react';

const SalaryPayments = () => {
  const [items, setItems] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterMode, setFilterMode] = useState('all');
  const [paymentDate, setPaymentDate] = useState('');
  const [ordering, setOrdering] = useState('-payment_date');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [nextPage, setNextPage] = useState(null);
  const [prevPage, setPrevPage] = useState(null);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  const [selected, setSelected] = useState(null);
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [isDeleteLoading, setIsDeleteLoading] = useState(false);
  const [isEditLoading, setIsEditLoading] = useState(false);
  const [isCreateLoading, setIsCreateLoading] = useState(false);
  const [isActionLoading, setIsActionLoading] = useState(false);
  const [isTaxFormOpen, setIsTaxFormOpen] = useState(false);

  const [employeeList, setEmployeeList] = useState([]);
  const [createForm, setCreateForm] = useState({
    employee: '',
    payment_date: '',
    salary_month: '',
    payment_mode: 'cash',
    amount: '',
    tds_deduction: '',
    professional_tax: '',
    employee_pf: '',
    employer_pf: '',
    employee_esi: '',
    employer_esi: '',
    labour_welfare_fund: '',
    others: '',
    net_pay: '',
    ctc: '',
    authorized_signatory: '',
    notes: '',
    status: 'draft'
  });
  const [editForm, setEditForm] = useState({
    employee: '',
    voucher_number: '',
    payment_date: '',
    salary_month: '',
    payment_mode: 'cash',
    amount: '',
    tds_deduction: '',
    professional_tax: '',
    employee_pf: '',
    employer_pf: '',
    employee_esi: '',
    employer_esi: '',
    labour_welfare_fund: '',
    others: '',
    net_pay: '',
    ctc: '',
    authorized_signatory: '',
    notes: '',
    status: 'draft'
  });

  const moneyFields = [
    'amount',
    'tds_deduction',
    'professional_tax',
    'employee_pf',
    'employer_pf',
    'employee_esi',
    'employer_esi',
    'labour_welfare_fund',
    'others',
    'net_pay',
    'ctc'
  ];

  const getErrorMessage = (err, fallback) => {
    const respData = err?.response?.data;
    const message =
      respData?.message ||
      respData?.detail ||
      respData?.error ||
      (typeof respData === 'string' ? respData : null) ||
      err?.message ||
      fallback;
    if (typeof message === 'string') return message;
    try {
      return JSON.stringify(message || fallback);
    } catch {
      return fallback;
    }
  };

  const getAuthHeaders = () => {
    const token = localStorage.getItem('token');
    return {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {})
    };
  };

  const formatCurrency = (value) =>
    `₹${(parseFloat(value || 0) || 0).toLocaleString('en-IN', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    })}`;

  const toMoney2 = (value, defaultValue = '0.00') => {
    const num = Number(value);
    if (Number.isNaN(num)) return defaultValue;
    return num.toFixed(2);
  };

  const exceedsMaxDigits = (moneyString) => {
    if (!moneyString) return false;
    const digitsOnly = moneyString.replace('.', '');
    return digitsOnly.length > 15;
  };

  const validateMoneyFields = (form) => {
    for (const field of moneyFields) {
      const val = toMoney2(form[field] ?? '');
      if (exceedsMaxDigits(val)) return false;
    }
    return true;
  };

  const fetchEmployees = async () => {
    try {
      const res = await axios.get(API_ENDPOINTS.EMPLOYEES_LIST, { headers: getAuthHeaders() });
      const data = res.data;
      const normalized = Array.isArray(data) ? data : data.results || data.data || [];
      setEmployeeList(normalized);
      return normalized; // Return the employees data
    } catch {
      // Ignore employee fetch errors to keep UI usable
      return [];
    }
  };

  // Helper function to get employee display name
  const getEmployeeDisplayName = (employee) => {
    if (!employee) return '';
    const empData = employee?.employee_data || {};
    const basicInfo = empData?.basic_information || {};
    return empData?.employee_name || 
           basicInfo?.full_name || 
           basicInfo?.employee_name ||
           employee.employee_name || 
           employee.name || 
           employee.full_name || 
           `Employee ${employee.id}`;
  };

  const fetchItems = async (page = 1) => {
    setIsLoading(true);
    setError('');
    try {
      const params = new URLSearchParams();
      if (filterStatus !== 'all') params.append('status', filterStatus);
      if (filterMode !== 'all') params.append('payment_mode', filterMode);
      if (paymentDate) params.append('payment_date', paymentDate);
      if (searchTerm) params.append('search', searchTerm);
      params.append('page', page);
      params.append('ordering', ordering || '-payment_date');

      const response = await axios.get(`${API_ENDPOINTS.SALARY_PAYMENTS_LIST}?${params.toString()}`, {
        headers: getAuthHeaders()
      });
      const data = response.data;
      const normalized = Array.isArray(data) ? data : data.results || data.data || [];
      const normalizedCount = Array.isArray(data) ? data.length : data.count || normalized.length || 0;

      setItems(normalized);
      setTotalCount(normalizedCount);
      setNextPage(Array.isArray(data) ? null : data.next);
      setPrevPage(Array.isArray(data) ? null : data.previous);
      setCurrentPage(page);
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to fetch salary payments'));
      setItems([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchEmployees();
    fetchItems(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchItems(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterStatus, filterMode, ordering, paymentDate]);

  useEffect(() => {
    const debounce = setTimeout(() => fetchItems(1), 400);
    return () => clearTimeout(debounce);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm]);

  const openCreate = () => {
    const today = new Date().toISOString().split('T')[0];
    setCreateForm({
      employee: '',
      payment_date: today,
      salary_month: today,
      payment_mode: 'cash',
      amount: '',
      tds_deduction: '',
      professional_tax: '',
      employee_pf: '',
      employer_pf: '',
      employee_esi: '',
      employer_esi: '',
      labour_welfare_fund: '',
      others: '',
      net_pay: '',
      ctc: '',
      authorized_signatory: '',
      notes: '',
      status: 'draft'
    });
    setIsCreateOpen(true);
    setError('');
  };

  const handleSubmitCreate = async (e) => {
    e.preventDefault();
    if (!createForm.employee) {
      setError('Please select an employee');
      return;
    }
    if (!validateMoneyFields(createForm)) {
      setError('Amounts must not exceed 15 total digits.');
      return;
    }
    setIsCreateLoading(true);
    try {
      const payload = {
        employee: parseInt(createForm.employee),
        payment_date: createForm.payment_date || null,
        salary_month: createForm.salary_month || null,
        payment_mode: createForm.payment_mode,
        amount: toMoney2(createForm.amount),
        tds_deduction: toMoney2(createForm.tds_deduction),
        professional_tax: toMoney2(createForm.professional_tax),
        employee_pf: toMoney2(createForm.employee_pf),
        employer_pf: toMoney2(createForm.employer_pf),
        employee_esi: toMoney2(createForm.employee_esi),
        employer_esi: toMoney2(createForm.employer_esi),
        labour_welfare_fund: toMoney2(createForm.labour_welfare_fund),
        others: toMoney2(createForm.others),
        net_pay: toMoney2(createForm.net_pay),
        ctc: toMoney2(createForm.ctc),
        authorized_signatory: createForm.authorized_signatory || null,
        notes: createForm.notes,
        status: createForm.status
      };
      const response = await axios.post(API_ENDPOINTS.SALARY_PAYMENTS_CREATE, payload, { headers: getAuthHeaders() });
      const msg = response?.data?.message || 'Salary payment created successfully';
      setSuccessMessage(msg);
      setIsCreateOpen(false);
      setTimeout(() => setSuccessMessage(''), 3000);
      fetchItems(currentPage);
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to create salary payment'));
    } finally {
      setIsCreateLoading(false);
    }
  };

  const handleView = async (row) => {
    try {
      setIsLoading(true);
      // Fetch employees to get employee names
      const employees = await fetchEmployees();
      const response = await axios.get(API_ENDPOINTS.SALARY_PAYMENTS_DETAIL(row.id), { headers: getAuthHeaders() });
      const data = response.data;
      
      // If employee is an object, extract the name; if it's an ID, find it in employees list
      let employeeName = data.employee_name || 'N/A';
      if (data.employee) {
        if (typeof data.employee === 'object' && data.employee.id) {
          const employee = employees.find(e => e.id === data.employee.id);
          employeeName = employee ? getEmployeeDisplayName(employee) : (data.employee.employee_data?.employee_name || data.employee.employee_name || 'N/A');
        } else if (typeof data.employee === 'number' || typeof data.employee === 'string') {
          const employee = employees.find(e => e.id === parseInt(data.employee));
          employeeName = employee ? getEmployeeDisplayName(employee) : 'N/A';
        }
      }
      
      // Add employee_name to the data for display
      setSelected({ ...data, employee_name: employeeName });
      setIsDetailsOpen(true);
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to fetch salary payment details'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleOpenEdit = async (row) => {
    try {
      setIsEditLoading(true);
      // Ensure employees are loaded first and get the data directly
      const employees = await fetchEmployees();
      const response = await axios.get(API_ENDPOINTS.SALARY_PAYMENTS_DETAIL(row.id), { headers: getAuthHeaders() });
      const data = response.data || row;
      setSelected(data);
      
      // Extract employee ID - handle both object and ID cases
      let employeeId = '';
      if (data.employee) {
        if (typeof data.employee === 'object' && data.employee.id) {
          employeeId = data.employee.id;
        } else if (typeof data.employee === 'number' || typeof data.employee === 'string') {
          employeeId = data.employee;
        }
      }
      
      // Verify employee exists in the list we just fetched
      const employeeExists = employees.find(e => String(e.id) === String(employeeId));
      if (employeeId && !employeeExists) {
        console.warn('Employee ID from salary payment not found in employees list:', {
          employeeId,
          employeeIdType: typeof employeeId,
          employeesCount: employees.length,
          employeeIds: employees.map(e => ({ id: e.id, name: getEmployeeDisplayName(e) })),
          salaryPaymentEmployeeName: data.employee_name,
          salaryPaymentData: data.employee
        });
      } else if (employeeId && employeeExists) {
        // Employee found, proceed with edit
      }
      
      setEditForm({
        employee: employeeId ? String(employeeId) : '',
        voucher_number: data.voucher_number || '',
        payment_date: data.payment_date ? data.payment_date.split('T')[0] : '',
        salary_month: data.salary_month ? data.salary_month.split('T')[0] : '',
        payment_mode: data.payment_mode || 'cash',
        amount: data.amount ?? '',
        tds_deduction: data.tds_deduction || '',
        professional_tax: data.professional_tax || '',
        employee_pf: data.employee_pf || '',
        employer_pf: data.employer_pf || '',
        employee_esi: data.employee_esi || '',
        employer_esi: data.employer_esi || '',
        labour_welfare_fund: data.labour_welfare_fund || '',
        others: data.others || '',
        net_pay: data.net_pay || '',
        ctc: data.ctc || '',
        authorized_signatory: data.authorized_signatory || '',
        notes: data.notes || '',
        status: data.status || 'draft'
      });
      setIsEditOpen(true);
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to load salary payment for editing'));
    } finally {
      setIsEditLoading(false);
    }
  };

  const handleSubmitEdit = async (e) => {
    e.preventDefault();
    if (!selected?.id) return;
    if (!editForm.employee) {
      setError('Please select an employee');
      return;
    }
    if (!validateMoneyFields(editForm)) {
      setError('Amounts must not exceed 15 total digits.');
      return;
    }
    setIsEditLoading(true);
    try {
      const payload = {
        employee: parseInt(editForm.employee),
        voucher_number: editForm.voucher_number || null,
        payment_date: editForm.payment_date || null,
        salary_month: editForm.salary_month || null,
        payment_mode: editForm.payment_mode,
        amount: toMoney2(editForm.amount),
        tds_deduction: toMoney2(editForm.tds_deduction),
        professional_tax: toMoney2(editForm.professional_tax),
        employee_pf: toMoney2(editForm.employee_pf),
        employer_pf: toMoney2(editForm.employer_pf),
        employee_esi: toMoney2(editForm.employee_esi),
        employer_esi: toMoney2(editForm.employer_esi),
        labour_welfare_fund: toMoney2(editForm.labour_welfare_fund),
        others: toMoney2(editForm.others),
        net_pay: toMoney2(editForm.net_pay),
        ctc: toMoney2(editForm.ctc),
        authorized_signatory: editForm.authorized_signatory || null,
        notes: editForm.notes,
        status: editForm.status
      };
      const response = await axios.put(
        API_ENDPOINTS.SALARY_PAYMENTS_UPDATE(selected.id),
        payload,
        { headers: getAuthHeaders() }
      );
      const msg = response?.data?.message || 'Salary payment updated successfully';
      setSuccessMessage(msg);
      setIsEditOpen(false);
      setSelected(null);
      setTimeout(() => setSuccessMessage(''), 3000);
      fetchItems(currentPage);
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to update salary payment'));
    } finally {
      setIsEditLoading(false);
    }
  };

  const handleOpenDelete = (row) => {
    setDeleteTarget(row);
    setIsDeleteOpen(true);
    setError('');
  };

  const handleConfirmDelete = async () => {
    if (!deleteTarget?.id) return;
    setIsDeleteLoading(true);
    try {
      const response = await axios.delete(API_ENDPOINTS.SALARY_PAYMENTS_DELETE(deleteTarget.id), {
        headers: getAuthHeaders()
      });
      const msg = response?.data?.message || 'Salary payment deleted successfully';
      setSuccessMessage(msg);
      setIsDeleteOpen(false);
      setDeleteTarget(null);
      fetchItems(currentPage);
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to delete salary payment'));
    } finally {
      setIsDeleteLoading(false);
    }
  };

  const doAction = async (id, endpoint, successFallback) => {
    setIsActionLoading(true);
    try {
      const response = await axios.post(endpoint(id), {}, { headers: getAuthHeaders() });
      const msg = response?.data?.message || response?.data?.status || successFallback;
      setSuccessMessage(msg);
      fetchItems(currentPage);
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      setError(getErrorMessage(err, 'Action failed'));
    } finally {
      setIsActionLoading(false);
    }
  };

  const handleMarkCompleted = (row) =>
    doAction(row.id, API_ENDPOINTS.SALARY_PAYMENTS_MARK_COMPLETED, 'Salary payment marked as completed');
  const handleCalculateNetPay = async (row) => {
    setIsActionLoading(true);
    try {
      const response = await axios.post(API_ENDPOINTS.SALARY_PAYMENTS_CALCULATE_NET_PAY(row.id), {}, { headers: getAuthHeaders() });
      const net = response?.data?.net_pay;
      const msg = response?.data?.message || `Net pay calculated${net ? `: ₹${net}` : ''}`;
      setSuccessMessage(msg);
      fetchItems(currentPage);
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to calculate net pay'));
    } finally {
      setIsActionLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const base = 'inline-flex items-center px-2 py-1 rounded-full text-xs font-medium';
    switch (status) {
      case 'completed':
        return `${base} bg-green-100 text-green-800`;
      case 'sent':
        return `${base} bg-blue-100 text-blue-800`;
      case 'draft':
      default:
        return `${base} bg-gray-100 text-gray-800`;
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="w-4 h-4" />;
      case 'sent':
        return <Check className="w-4 h-4" />;
      default:
        return <FileText className="w-4 h-4" />;
    }
  };

  return (
    <div className="space-y-6">
      {/* Notifications */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-md flex items-center justify-between">
          <div className="flex items-center">
            <AlertCircle className="w-5 h-5 mr-2" />
            <span>{error}</span>
          </div>
          <button onClick={() => setError('')} className="text-red-600 hover:text-red-800">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}
      {successMessage && (
        <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-md flex items-center justify-between">
          <div className="flex items-center">
            <CheckCircle className="w-5 h-5 mr-2" />
            <span>{successMessage}</span>
          </div>
          <button onClick={() => setSuccessMessage('')} className="text-green-600 hover:text-green-800">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-white p-6 rounded-lg border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-muted-foreground">Total Salary Vouchers</p>
              <p className="text-2xl font-bold text-foreground">{totalCount}</p>
            </div>
            <div className="p-3 bg-blue-100 rounded-full">
              <FileText className="w-6 h-6 text-blue-600" />
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-muted-foreground">Draft</p>
              <p className="text-2xl font-bold text-foreground">
                {items.filter((q) => q.status === 'draft').length}
              </p>
            </div>
            <div className="p-3 bg-gray-100 rounded-full">
              <FileText className="w-6 h-6 text-gray-600" />
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-muted-foreground">Sent</p>
              <p className="text-2xl font-bold text-foreground">
                {items.filter((q) => q.status === 'sent').length}
              </p>
            </div>
            <div className="p-3 bg-blue-100 rounded-full">
              <Check className="w-6 h-6 text-blue-600" />
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-muted-foreground">Completed</p>
              <p className="text-2xl font-bold text-foreground">
                {items.filter((q) => q.status === 'completed').length}
              </p>
            </div>
            <div className="p-3 bg-green-100 rounded-full">
              <CheckCircle className="w-6 h-6 text-green-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Filters and Search */}
      <div className="bg-white p-6 rounded-lg border border-gray-200">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <input
              type="text"
              placeholder="Search salary payments..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
            />
          </div>
          <input
            type="date"
            value={paymentDate}
            onChange={(e) => setPaymentDate(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
          />
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
          >
            <option value="all">All Status</option>
            <option value="draft">Draft</option>
            <option value="sent">Sent</option>
            <option value="completed">Completed</option>
          </select>
          <select
            value={filterMode}
            onChange={(e) => setFilterMode(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent capitalize"
          >
            <option value="all">All Modes</option>
            <option value="cash">Cash</option>
            <option value="payable">Payable</option>
            <option value="bank_transfer">Bank Transfer</option>
          </select>
          <select
            value={ordering}
            onChange={(e) => setOrdering(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
          >
            <option value="-payment_date">Date: Newest first</option>
            <option value="payment_date">Date: Oldest first</option>
            <option value="-amount">Amount: High to Low</option>
            <option value="amount">Amount: Low to High</option>
          </select>
          <Button onClick={() => setIsTaxFormOpen(true)}>
            <Plus className="w-4 h-4 mr-2" />
            New Salary Payment
          </Button>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg border border-gray-200">
        <div className="overflow-x-auto">
          {isLoading ? (
            <div className="p-12 text-center">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
              <p className="mt-4 text-muted-foreground">Loading salary payments...</p>
            </div>
          ) : items.length === 0 ? (
            <div className="p-12 text-center">
              <FileText className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <p className="text-muted-foreground">No salary payments found</p>
            </div>
          ) : (
            <>
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Voucher</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Employee</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Payment Date</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Mode</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Net Pay</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {items.map((row) => (
                    <tr key={row.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="w-10 h-10 bg-primary rounded-lg flex items-center justify-center mr-3">
                            <FileText className="w-5 h-5 text-white" />
                          </div>
                          <div>
                            <div className="text-sm font-medium text-gray-900">{row.voucher_number || 'N/A'}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">{row.employee_name || 'N/A'}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">{formatCurrency(row.amount)}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {row.payment_date ? new Date(row.payment_date).toLocaleDateString() : 'N/A'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 capitalize">
                        {row.payment_mode || 'N/A'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">{formatCurrency(row.net_pay)}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={getStatusBadge(row.status)}>
                          {getStatusIcon(row.status)}
                          <span className="ml-1 capitalize">{row.status || 'N/A'}</span>
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex flex-wrap gap-2">
                          <Button variant="ghost" size="sm" onClick={() => handleView(row)} title="View">
                            <Eye className="w-4 h-4" />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => handleOpenEdit(row)} title="Edit">
                            <Edit className="w-4 h-4" />
                          </Button>
                          {row.status !== 'completed' && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleMarkCompleted(row)}
                              title="Mark Completed"
                              disabled={isActionLoading}
                            >
                              <Check className="w-4 h-4" />
                            </Button>
                          )}
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleCalculateNetPay(row)}
                            title="Calculate Net Pay"
                            disabled={isActionLoading}
                          >
                            <CalculatorIcon />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => handleOpenDelete(row)} title="Delete">
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {(nextPage || prevPage) && (
                <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
                  <div className="text-sm text-gray-700">Showing page {currentPage} of {Math.ceil(totalCount / 10)}</div>
                  <div className="flex space-x-2">
                    <Button variant="outline" size="sm" onClick={() => fetchItems(currentPage - 1)} disabled={!prevPage}>
                      Previous
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => fetchItems(currentPage + 1)} disabled={!nextPage}>
                      Next
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Details Modal */}
      {isDetailsOpen && selected && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl border border-gray-200 max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-foreground">Salary Payment Details</h3>
                <p className="text-sm text-muted-foreground">Voucher #{selected.voucher_number || 'N/A'}</p>
              </div>
              <Button variant="outline" onClick={() => { setIsDetailsOpen(false); setSelected(null); }}>
                Close
              </Button>
            </div>
            <div className="p-6 space-y-4 text-sm">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div><p className="text-muted-foreground">Employee</p><p className="font-semibold">{selected.employee_name || 'N/A'}</p></div>
                <div><p className="text-muted-foreground">Status</p><p className="font-semibold capitalize">{selected.status || 'N/A'}</p></div>
                <div><p className="text-muted-foreground">Payment Mode</p><p className="font-semibold capitalize">{selected.payment_mode || 'N/A'}</p></div>
                <div><p className="text-muted-foreground">Payment Date</p><p className="font-semibold">{selected.payment_date ? new Date(selected.payment_date).toLocaleDateString() : 'N/A'}</p></div>
                <div><p className="text-muted-foreground">Salary Month</p><p className="font-semibold">{selected.salary_month ? new Date(selected.salary_month).toLocaleDateString() : 'N/A'}</p></div>
                <div><p className="text-muted-foreground">Amount</p><p className="font-semibold">{formatCurrency(selected.amount)}</p></div>
                <div><p className="text-muted-foreground">Net Pay</p><p className="font-semibold">{formatCurrency(selected.net_pay)}</p></div>
                <div><p className="text-muted-foreground">CTC</p><p className="font-semibold">{formatCurrency(selected.ctc)}</p></div>
                <div><p className="text-muted-foreground">TDS Deduction</p><p className="font-semibold">{formatCurrency(selected.tds_deduction)}</p></div>
                <div><p className="text-muted-foreground">Professional Tax</p><p className="font-semibold">{formatCurrency(selected.professional_tax)}</p></div>
                <div><p className="text-muted-foreground">Employee PF</p><p className="font-semibold">{formatCurrency(selected.employee_pf)}</p></div>
                <div><p className="text-muted-foreground">Employer PF</p><p className="font-semibold">{formatCurrency(selected.employer_pf)}</p></div>
                <div><p className="text-muted-foreground">Employee ESI</p><p className="font-semibold">{formatCurrency(selected.employee_esi)}</p></div>
                <div><p className="text-muted-foreground">Employer ESI</p><p className="font-semibold">{formatCurrency(selected.employer_esi)}</p></div>
                <div><p className="text-muted-foreground">Labour Welfare Fund</p><p className="font-semibold">{formatCurrency(selected.labour_welfare_fund)}</p></div>
                <div><p className="text-muted-foreground">Others</p><p className="font-semibold">{formatCurrency(selected.others)}</p></div>
                <div><p className="text-muted-foreground">Authorized Signatory</p><p className="font-semibold">{selected.authorized_signatory || 'N/A'}</p></div>
                <div><p className="text-muted-foreground">Created</p><p className="font-semibold">{selected.created_at ? new Date(selected.created_at).toLocaleString() : 'N/A'}</p></div>
                <div><p className="text-muted-foreground">Updated</p><p className="font-semibold">{selected.updated_at ? new Date(selected.updated_at).toLocaleString() : 'N/A'}</p></div>
              </div>

              {selected.notes && (
                <div>
                  <p className="text-sm font-semibold text-foreground">Notes</p>
                  <p className="text-sm text-muted-foreground bg-gray-50 border border-gray-200 rounded p-3">{selected.notes}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {isTaxFormOpen && (
        <TaxInvoiceForm
          isOpen={isTaxFormOpen}
          onClose={() => setIsTaxFormOpen(false)}
          onSave={() => { setIsTaxFormOpen(false); fetchItems(currentPage); }}
          invoiceType="salary-payment"
        />
      )}

      {/* Create Modal */}
      {isCreateOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl border border-gray-200 max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-foreground">Create Salary Payment</h3>
              <Button variant="outline" onClick={() => setIsCreateOpen(false)}>Close</Button>
            </div>
            <form onSubmit={handleSubmitCreate} className="p-6 space-y-6">
              <div className="space-y-4">
                <h3 className="text-lg font-medium text-foreground">Basic Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Employee <span className="text-red-500">*</span></label>
                  <select
                    value={String(createForm.employee || '')}
                    onChange={(e) => setCreateForm((p) => ({ ...p, employee: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                  >
                    <option value="">Select employee</option>
                    {employeeList.map((s) => (
                      <option key={s.id} value={String(s.id)}>
                        {getEmployeeDisplayName(s)}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Payment Date</label>
                  <input
                    type="date"
                    value={createForm.payment_date}
                    onChange={(e) => setCreateForm((p) => ({ ...p, payment_date: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Salary Month</label>
                  <input
                    type="date"
                    value={createForm.salary_month}
                    onChange={(e) => setCreateForm((p) => ({ ...p, salary_month: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Payment Mode</label>
                  <select
                    value={createForm.payment_mode}
                    onChange={(e) => setCreateForm((p) => ({ ...p, payment_mode: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary capitalize"
                  >
                    <option value="cash">Cash</option>
                    <option value="payable">Payable</option>
                    <option value="bank_transfer">Bank Transfer</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                  <select
                    value={createForm.status}
                    onChange={(e) => setCreateForm((p) => ({ ...p, status: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                  >
                    <option value="draft">Draft</option>
                    <option value="sent">Sent</option>
                    <option value="completed">Completed</option>
                  </select>
                </div>
                </div>
              </div>

              <div className="space-y-4">
                <h3 className="text-lg font-medium text-foreground">Salary Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Amount (Gross)</label>
                    <input type="number" step="0.01" value={createForm.amount} onChange={(e) => setCreateForm((p) => ({ ...p, amount: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">CTC</label>
                    <input type="number" step="0.01" value={createForm.ctc} onChange={(e) => setCreateForm((p) => ({ ...p, ctc: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Net Pay</label>
                    <input type="number" step="0.01" value={createForm.net_pay} onChange={(e) => setCreateForm((p) => ({ ...p, net_pay: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary" />
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                <h3 className="text-lg font-medium text-foreground">Deductions</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">TDS / Income Tax</label>
                    <input type="number" step="0.01" value={createForm.tds_deduction} onChange={(e) => setCreateForm((p) => ({ ...p, tds_deduction: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Professional Tax</label>
                    <input type="number" step="0.01" value={createForm.professional_tax} onChange={(e) => setCreateForm((p) => ({ ...p, professional_tax: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Employee PF</label>
                    <input type="number" step="0.01" value={createForm.employee_pf} onChange={(e) => setCreateForm((p) => ({ ...p, employee_pf: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Employer PF</label>
                    <input type="number" step="0.01" value={createForm.employer_pf} onChange={(e) => setCreateForm((p) => ({ ...p, employer_pf: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Employee ESI</label>
                    <input type="number" step="0.01" value={createForm.employee_esi} onChange={(e) => setCreateForm((p) => ({ ...p, employee_esi: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Employer ESI</label>
                    <input type="number" step="0.01" value={createForm.employer_esi} onChange={(e) => setCreateForm((p) => ({ ...p, employer_esi: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Labour Welfare Fund</label>
                    <input type="number" step="0.01" value={createForm.labour_welfare_fund} onChange={(e) => setCreateForm((p) => ({ ...p, labour_welfare_fund: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Others</label>
                    <input type="number" step="0.01" value={createForm.others} onChange={(e) => setCreateForm((p) => ({ ...p, others: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary" />
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                <h3 className="text-lg font-medium text-foreground">Payment Details</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Authorized Signatory</label>
                    <input
                      type="text"
                      value={createForm.authorized_signatory}
                      onChange={(e) => setCreateForm((p) => ({ ...p, authorized_signatory: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                    />
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                <textarea
                  value={createForm.notes}
                  onChange={(e) => setCreateForm((p) => ({ ...p, notes: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>

              <div className="flex justify-end space-x-3">
                <Button variant="outline" type="button" onClick={() => setIsCreateOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" disabled={isCreateLoading}>
                  {isCreateLoading ? 'Creating...' : 'Create'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {isEditOpen && selected && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl border border-gray-200 max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-foreground">Edit Salary Payment</h3>
              <Button variant="outline" onClick={() => setIsEditOpen(false)}>Close</Button>
            </div>
            <form onSubmit={handleSubmitEdit} className="p-6 space-y-6">
              <div className="space-y-4">
                <h3 className="text-lg font-medium text-foreground">Basic Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Employee <span className="text-red-500">*</span></label>
                  <select
                    value={String(editForm.employee || '')}
                    onChange={(e) => setEditForm((p) => ({ ...p, employee: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                  >
                    <option value="">Select employee</option>
                    {employeeList.map((s) => (
                      <option key={s.id} value={String(s.id)}>
                        {getEmployeeDisplayName(s)}
                      </option>
                    ))}
                    {/* Show salary payment employee name if employee ID exists but not in list */}
                    {selected && editForm.employee && 
                     !employeeList.find(e => String(e.id) === String(editForm.employee)) && 
                     selected.employee_name && (
                      <option value={String(editForm.employee)}>
                        {selected.employee_name}
                      </option>
                    )}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Voucher Number</label>
                  <input
                    type="text"
                    value={editForm.voucher_number}
                    onChange={(e) => setEditForm((p) => ({ ...p, voucher_number: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Payment Date</label>
                  <input
                    type="date"
                    value={editForm.payment_date}
                    onChange={(e) => setEditForm((p) => ({ ...p, payment_date: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Salary Month</label>
                  <input
                    type="date"
                    value={editForm.salary_month}
                    onChange={(e) => setEditForm((p) => ({ ...p, salary_month: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Payment Mode</label>
                  <select
                    value={editForm.payment_mode}
                    onChange={(e) => setEditForm((p) => ({ ...p, payment_mode: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary capitalize"
                  >
                    <option value="cash">Cash</option>
                    <option value="payable">Payable</option>
                    <option value="bank_transfer">Bank Transfer</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                  <select
                    value={editForm.status}
                    onChange={(e) => setEditForm((p) => ({ ...p, status: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                  >
                    <option value="draft">Draft</option>
                    <option value="sent">Sent</option>
                    <option value="completed">Completed</option>
                  </select>
                </div>
                </div>
              </div>

              <div className="space-y-4">
                <h3 className="text-lg font-medium text-foreground">Salary Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Amount (Gross)</label>
                    <input type="number" step="0.01" value={editForm.amount} onChange={(e) => setEditForm((p) => ({ ...p, amount: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">CTC</label>
                    <input type="number" step="0.01" value={editForm.ctc} onChange={(e) => setEditForm((p) => ({ ...p, ctc: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Net Pay</label>
                    <input type="number" step="0.01" value={editForm.net_pay} onChange={(e) => setEditForm((p) => ({ ...p, net_pay: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary" />
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                <h3 className="text-lg font-medium text-foreground">Deductions</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">TDS / Income Tax</label>
                    <input type="number" step="0.01" value={editForm.tds_deduction} onChange={(e) => setEditForm((p) => ({ ...p, tds_deduction: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Professional Tax</label>
                    <input type="number" step="0.01" value={editForm.professional_tax} onChange={(e) => setEditForm((p) => ({ ...p, professional_tax: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Employee PF</label>
                    <input type="number" step="0.01" value={editForm.employee_pf} onChange={(e) => setEditForm((p) => ({ ...p, employee_pf: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Employer PF</label>
                    <input type="number" step="0.01" value={editForm.employer_pf} onChange={(e) => setEditForm((p) => ({ ...p, employer_pf: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Employee ESI</label>
                    <input type="number" step="0.01" value={editForm.employee_esi} onChange={(e) => setEditForm((p) => ({ ...p, employee_esi: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Employer ESI</label>
                    <input type="number" step="0.01" value={editForm.employer_esi} onChange={(e) => setEditForm((p) => ({ ...p, employer_esi: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Labour Welfare Fund</label>
                    <input type="number" step="0.01" value={editForm.labour_welfare_fund} onChange={(e) => setEditForm((p) => ({ ...p, labour_welfare_fund: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Others</label>
                    <input type="number" step="0.01" value={editForm.others} onChange={(e) => setEditForm((p) => ({ ...p, others: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary" />
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                <h3 className="text-lg font-medium text-foreground">Payment Details</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Authorized Signatory</label>
                  <input
                    type="text"
                    value={editForm.authorized_signatory}
                    onChange={(e) => setEditForm((p) => ({ ...p, authorized_signatory: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                </div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                <textarea
                  value={editForm.notes}
                  onChange={(e) => setEditForm((p) => ({ ...p, notes: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>

              <div className="flex justify-end space-x-3">
                <Button variant="outline" type="button" onClick={() => setIsEditOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" disabled={isEditLoading}>
                  {isEditLoading ? 'Updating...' : 'Update'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation */}
      {isDeleteOpen && deleteTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md border border-gray-200">
            <div className="px-6 py-4 border-b border-gray-100">
              <h3 className="text-lg font-semibold text-foreground">Delete Salary Payment</h3>
              <p className="text-sm text-muted-foreground mt-1">
                Are you sure you want to delete voucher {deleteTarget.voucher_number || deleteTarget.id}?
              </p>
            </div>
            <div className="px-6 py-4 flex justify-end space-x-3">
              <Button variant="outline" onClick={() => setIsDeleteOpen(false)}>Cancel</Button>
              <Button variant="destructive" onClick={handleConfirmDelete} disabled={isDeleteLoading}>
                {isDeleteLoading ? 'Deleting...' : 'Delete'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const CalculatorIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 8h6M9 12h6m-6 4h6M5 5h14v14H5z" />
  </svg>
);

export default SalaryPayments;


